import type { KeysToCamelCase, KeysToSnakeCase } from '@/types/app';
import type { OptionableDictTypes, TDict } from '@/types/dicts';
import type { FileType } from '@/types/uploadFiles';

import { DictTypes, SecurityStatus } from '../constants';
import type { TRequestClient } from '../types/request';
import { splitBase64 } from './helpers/strings';

export const validateNumberInputValue = (
  value: string,
  maxLength?: number,
  format?: boolean
) => {
  let filteredValue =
    value === '0' ? '0' : value?.toString()?.replace(/[^0-9]/g, '');
  if (maxLength) {
    filteredValue = filteredValue?.slice(0, maxLength);
  }
  if (format) {
    filteredValue = filteredValue?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
  return filteredValue;
};

export const setDictionaryOptions = (
  dictSnakeName: OptionableDictTypes,
  dicts: TDict | null
) => {
  if (!dicts || !dicts[dictSnakeName]) return [];

  const optionsArray = dicts[dictSnakeName].content.map((option) => {
    const result = {
      value: option.code,
      label: option.name,
      product_type_uuid: option.uuid || null,
      is_active: true
    };
    if ('product_type_uuid' in option && option.product_type_uuid) {
      result.product_type_uuid = option.product_type_uuid;
    }
    if ('is_active' in option) {
      result.is_active = option.is_active ?? result.is_active;
    }
    return result;
  });

  if (dictSnakeName === DictTypes.Dealers) {
    return optionsArray
      ?.filter((data) => data.is_active)
      ?.sort((a, b) => {
        if (a.label.includes('Chevrolet') && !b.label.includes('Chevrolet')) {
          return 1;
        } else if (
          !a.label.includes('Chevrolet') &&
          b.label.includes('Chevrolet')
        ) {
          return -1;
        } else if (a.label.includes('Almaty') && !b.label.includes('Almaty')) {
          return -1;
        } else if (!a.label.includes('Almaty') && b.label.includes('Almaty')) {
          return 1;
        } else if (a.label.includes('Astana') && !b.label.includes('Astana')) {
          return -1;
        } else if (!a.label.includes('Astana') && b.label.includes('Astana')) {
          return 1;
        } else {
          return 0;
        }
      });
  } else if (dictSnakeName === DictTypes.AutoTypes) {
    return optionsArray?.sort((a, b) => {
      if (!a.label.includes('Новое Авто') && b.label.includes('Новое Авто')) {
        return 1;
      } else if (
        a.label.includes('Новое Авто') &&
        !b.label.includes('Новое Авто')
      ) {
        return -1;
      } else {
        return 0;
      }
    });
  } else if (dictSnakeName === DictTypes.EngineTypes) {
    return optionsArray?.sort((a, b) => {
      if (a.label.includes('Бензин')) {
        return -1;
      } else if (b.label.includes('Бензин')) {
        return 1;
      } else {
        return a.label.localeCompare(b.label);
      }
    });
  } else if (dictSnakeName === DictTypes.DocumentTypes) {
    return optionsArray?.sort((a, b) => {
      if (a.label.includes('Удостоверение личности')) {
        return -1;
      } else if (b.label.includes('Удостоверение личности')) {
        return 1;
      } else {
        return a.label.localeCompare(b.label);
      }
    });
  } else if (dictSnakeName === DictTypes.SocialStatuses) {
    return optionsArray?.sort((a, b) => {
      if (a.label.includes('Стандартный профиль')) {
        return -1;
      } else if (b.label.includes('Стандартный профиль')) {
        return 1;
      } else {
        return a.label.localeCompare(b.label);
      }
    });
  } else if (dictSnakeName === DictTypes.Products) {
    const productTypesDict = dicts?.[DictTypes.ProductsTypes];

    const insuranceProduct = productTypesDict?.content?.find(
      (data) => data.name === 'Страховка'
    );
    return optionsArray?.filter((option) => {
      return option.product_type_uuid === insuranceProduct?.uuid;
    });
  } else {
    return optionsArray;
  }
};

export const setCustomOption = (
  content: {
    code: number | string;
    name: string;
    uuid: string;
    brand_uuid?: string;
  }[],
  useUuid = false
) => {
  const optionsArray = content.map((data) => {
    return {
      value: useUuid ? data.uuid : data.code.toString(),
      label: data.name
    };
  });
  return optionsArray;
};

export const getLastRequestDate = (clintRequests: TRequestClient[]) => {
  if (clintRequests?.length > 0) {
    return clintRequests[clintRequests.length - 1]?.create_date || '';
  }
  return '';
};

export const generateRandomSixDiginCode = () => {
  const randomCode = Math.floor(100000 + Math.random() * 900000);
  return randomCode.toString();
};

/**
 * Конвертирует строку в camelCase.
 * Поддерживает форматы snake_case, kebab-case, PascalCase и camelCase.
 *
 * @param {string} str - Строка для преобразования.
 * @returns {string} - Строка, приведённая к camelCase.
 *
 * @example
 * stringToCamelCase('example_string');
 * // Returns: 'exampleString'
 *
 * stringToCamelCase('ExampleString');
 * // Returns: 'exampleString'
 *
 * stringToCamelCase('example-string');
 * // Returns: 'exampleString'
 *
 * stringToCamelCase('exampleString');
 * // Returns: 'exampleString'
 */
export const stringToCamelCase = (str: string) => {
  return str
    .replace(/(\w)?[_-](\w)/g, (_, c, d) => {
      if (!c) return d.toUpperCase();
      return c.toLowerCase() + d.toUpperCase();
    })
    .replace(/^\w/, (c) => c.toLowerCase());
};

/**
 * Рекурсивно конвертирует ключи объектов и массивов в camelCase.
 * Значения примитивных типов (string, number, boolean и т.д.) не изменяются.
 *
 * @template T - Тип входного значения
 * @param {T} input - Объект, массив или значение для преобразования.
 * @returns {KeysToCamelCase<T>} Новый объект/массив с ключами в camelCase.
 *
 * @example
 * const obj = {
 *   snake_case_key: 1,
 *   PascalKey: 2,
 *   nested_object: { another_snake_case: 3 },
 *   snake_array: [{ test_value: 4 }],
 * };
 *
 * keysToCamelCase(obj);
 * // Returns:
 * // {
 * //   snakeCaseKey: 1,
 * //   pascalKey: 2,
 * //   nestedObject: { anotherSnakeCase: 3 },
 * //   snakeArray: [{ testValue: 4 }]
 * // }
 */
export const keysToCamelCase = <T>(input: T): KeysToCamelCase<T> => {
  if (Array.isArray(input)) {
    return input.map((item) => keysToCamelCase(item)) as KeysToCamelCase<T>;
  }

  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const newObj: Record<string, unknown> = {};
    Object.entries(input).forEach(([key, value]) => {
      newObj[stringToCamelCase(key)] =
        value && typeof value === 'object' ? keysToCamelCase(value) : value;
    });
    return newObj as KeysToCamelCase<T>;
  }

  return input as KeysToCamelCase<T>;
};

/**
 * Конвертирует строку в snake_case.
 * Поддерживает форматы camelCase, PascalCase, kebab-case и snake_case.
 *
 * @param {string} str - Строка для преобразования.
 * @returns {string} - Строка, приведённая к snake_case.
 *
 * @example
 * stringToSnakeCase('exampleString');
 * // Returns: 'example_string'
 *
 * stringToSnakeCase('ExampleString');
 * // Returns: 'example_string'
 *
 * stringToSnakeCase('example-string');
 * // Returns: 'example_string'
 *
 * stringToSnakeCase('example_string');
 * // Returns: 'example_string'
 */
export const stringToSnakeCase = (str: string): string => {
  return (
    str
      // Нормализуем разделители: "-" и пробелы → "_"
      .replace(/[-\s]+/g, '_')
      // HTTPServer → HTTP_Server (граница между серией верхних и "Верхняя+нижняя/цифра")
      .replace(/([A-Z]+)([A-Z][a-z0-9])/g, '$1_$2')
      // fooBar → foo_Bar (граница "нижняя/цифра → верхняя")
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      // Схлопываем двойные "__" и убираем крайние
      .replace(/__+/g, '_')
      .replace(/^_+|_+$/g, '')
      // Всё в нижний регистр
      .toLowerCase()
  );
};

/**
 * Рекурсивно конвертирует ключи объектов и массивов в snake_case.
 * Значения примитивных типов (string, number, boolean и т.д.) не изменяются.
 *
 * @template T - Тип входного значения
 * @param {T} input - Объект, массив или значение для преобразования.
 * @returns {KeysToSnakeCase<T>} Новый объект/массив с ключами в snake_case.
 *
 * @example
 * const obj = {
 *   camelCaseKey: 1,
 *   PascalKey: 2,
 *   nestedObject: { anotherCamelCase: 3 },
 *   camelArray: [{ testValue: 4 }],
 * };
 *
 * keysToSnakeCase(obj);
 * // Returns:
 * // {
 * //   camel_case_key: 1,
 * //   pascal_key: 2,
 * //   nested_object: { another_camel_case: 3 },
 * //   camel_array: [{ test_value: 4 }]
 * // }
 */
export const keysToSnakeCase = <T>(input: T): KeysToSnakeCase<T> => {
  if (Array.isArray(input)) {
    return input.map((item) => keysToSnakeCase(item)) as KeysToSnakeCase<T>;
  }

  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const newObj: Record<string, unknown> = {};
    Object.entries(input).forEach(([key, value]) => {
      newObj[stringToSnakeCase(key)] =
        value && typeof value === 'object' ? keysToSnakeCase(value) : value;
    });
    return newObj as KeysToSnakeCase<T>;
  }

  return input as KeysToSnakeCase<T>;
};

export function getTimeAgo(
  dateString: Date | string | null | undefined
): string {
  if (!dateString) return 'Неверная дата';
  const pastDate = new Date(dateString);
  if (isNaN(pastDate.getTime())) {
    return 'Неверная дата';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - pastDate.getTime()) / 1000);

  if (diffInSeconds < 10) {
    return 'Только что';
  }

  if (diffInSeconds < 60) {
    return `${diffInSeconds} сек. назад`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} мин. назад`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ч. назад`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  const remainingHours = diffInHours % 24;

  if (diffInDays > 7 || remainingHours === 0) {
    return `${diffInDays} дн. назад`;
  }

  return `${diffInDays} дн. ${remainingHours} ч. назад`;
}

export const getFileExtension = (file: {
  name: string;
}): { extension: string; isImage: boolean } => {
  const extension: string = file.name.split('.').pop()?.toLowerCase() || '';

  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
    extension
  );

  return { extension, isImage };
};

export const isSecurityStatus = (
  statusCode: string | number
): statusCode is SecurityStatus => {
  return Object.values(SecurityStatus).includes(statusCode as SecurityStatus);
};

export const isValidUrl = (url: string | null): boolean => {
  if (!url || typeof url !== 'string') return false;

  // Запрещаем любые пробельные символы
  if (/[\s\n\r\t]/.test(url)) return false;

  try {
    const parsed = new URL(url);

    if (!parsed.protocol) return false;

    const protocol = parsed.protocol.toLowerCase(); // например "http:", "mailto:"

    // Запрещаем data: URI (base64 и прочие data-ссылки)
    if (protocol === 'data:') return false;

    // === Критическая проверка: отклоняем http:example.com и https:/example.com ===
    // Разрешаем :// только для протоколов, которые его используют
    if (
      protocol === 'http:' ||
      protocol === 'https:' ||
      protocol === 'ftp:' ||
      protocol === 'ws:' ||
      protocol === 'wss:'
    ) {
      // Должен быть именно "://", а не ":example" или ":/example"
      if (!url.startsWith(protocol + '//')) {
        return false;
      }
    }

    // Для обычных веб-протоколов hostname обязателен
    if (!parsed.hostname && !isSpecialProtocol(protocol)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Протоколы, которые не требуют hostname или могут быть без //
 */
const isSpecialProtocol = (protocol: string): boolean => {
  const p = protocol.replace(':', '').toLowerCase();
  return [
    'mailto',
    'tel',
    'sms',
    'blob',
    'file',
    'about',
    'javascript',
    'intent'
  ].includes(p);
};
/**
 * Возвращает массив уникальных элементов, оставляя для каждого уникального значения
 * по указанному ключу только самый новый элемент (по ключу сортировки).
 * Результат отсортирован по убыванию значения ключа сортировки (от новых к старым).
 *
 * @template T - Тип элементов массива
 * @param {T[]} array - Массив элементов для обработки
 * @param {keyof T} uniqueKey - Ключ для определения уникальности элементов
 * @param {keyof T} sortKey - Ключ для сортировки (должен содержать дату или число)
 * @returns {T[]} Массив уникальных элементов, отсортированный по убыванию sortKey
 *
 * @example
 * const items = [
 *   { id: 1, name: 'Item 1', updatedAt: '2024-01-01' },
 *   { id: 1, name: 'Item 1 Updated', updatedAt: '2024-01-02' },
 *   { id: 2, name: 'Item 2', updatedAt: '2024-01-01' },
 * ];
 *
 * getNewestValues(items, 'id', 'updatedAt');
 * // Returns: [
 * //   { id: 1, name: 'Item 1 Updated', updatedAt: '2024-01-02' },
 * //   { id: 2, name: 'Item 2', updatedAt: '2024-01-01' }
 * // ]
 */
export function getNewestValues<T>(
  array: T[],
  uniqueKey: keyof T,
  sortKey: keyof T
): T[] {
  if (!array || !Array.isArray(array) || array.length === 0) {
    return [];
  }

  // Создаем копию массива для избежания мутации исходного
  const sortedArray = [...array].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];

    // Преобразуем значения в даты для сравнения
    const aDate = new Date(aValue as string | number | Date).getTime();
    const bDate = new Date(bValue as string | number | Date).getTime();

    // Сортируем по убыванию (новые сначала)
    return bDate - aDate;
  });

  // Используем Map для хранения уникальных элементов
  // При встрече дубликата по uniqueKey, оставляем первый (самый новый после сортировки)
  const uniqueMap = new Map<unknown, T>();

  for (const item of sortedArray) {
    const keyValue = item[uniqueKey];
    if (!uniqueMap.has(keyValue)) {
      uniqueMap.set(keyValue, item);
    }
  }

  // Возвращаем значения Map в виде отсортированного массива
  return Array.from(uniqueMap.values());
}

/**
 * Генерирует случайный ID с префиксом и временной меткой.
 *
 * @param {string} prefix - Префикс для ID.
 * @returns {string} Случайный ID в формате: [prefix]-[timestamp]-[random1]-[random2]
 *
 * @example
 * generateRandomId('my-prefix');
 * // Returns: 'my-prefix-1718563200-3wz4-5v67'
 * */
export const generateRandomId = (prefix?: string): string => {
  const result: (string | number)[] = [];
  if (prefix) {
    result.push(prefix);
  }
  result.push(Date.now().toString());
  result.push(Math.random().toString(36).substring(2, 15));
  result.push(Math.random().toString(36).substring(2, 15));
  return result.join('-');
};

export const imgSrcMapperToForm = (
  imgSrc: string | null | undefined
): FileType => {
  if (!imgSrc) return { base64: '', format: '', name: '', size: 0, url: '' };

  const isImageSrcUrl = isValidUrl(imgSrc);
  const { format, base64 } = splitBase64(imgSrc);
  return {
    base64: !isImageSrcUrl ? base64 : '',
    format: !isImageSrcUrl ? format : '',
    name: '',
    size: 0,
    url: isImageSrcUrl ? imgSrc : ''
  };
};
